<script lang="ts">
  import type { App } from '$lib/types';
  import { getContext, onDestroy, createEventDispatcher } from 'svelte';
  import {
    DISCORD_AUTH_URL,
    TWITCH_AUTH_URL,
    GOOGLE_AUTH_URL,
    APPLE_CLIENT_ID,
    APPLE_SCOPES,
    APPLE_REDIRECT,
    FACEBOOK_AUTH_URL,
    AVAILABLE_EXTERNALS,
    PATREON_AUTH_URL
  } from '$lib/constants';
  import { Toasts } from '$lib/stores';
  import { analytics, includeScript, uuidv4 } from '$lib/utils';
  import { captureException } from '@sentry/sveltekit';
  import { PUBLIC_STAGING_ID } from '$env/static/public';
  import { getApolloClient, gql } from '$lib/apollo';

  import { doWork } from '$routes/auth/external/[...provider]/+page.svelte';

  import { faDiscord, faTwitch, faApple, faPaypal, faFacebook, faPatreon } from '@fortawesome/free-brands-svg-icons';
  import Google from '$assets/svgs/google_colored.svg';
  import IconButton from '$mds/Buttons/IconButton';
  import { DISCORD_GUILD_AUTH_URL } from '$lib/constants/oauth';

  export let provider: keyof typeof AVAILABLE_EXTERNALS;
  export let providerParams = [];
  export let type = 'login';
  export let accountId: string | null = null;
  export let disabled = false;
  let className = '';
  export { className as class };
  export let transparent = false;

  const dispatch = createEventDispatcher();
  const { irkAccount, refreshSession } = getContext<App>('app');
  let loading = false;
  let popup: Window | null = null;

  async function includeAppleJS() {
    try {
      await includeScript('https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js');
    } catch (error) {
      // ...
    }
  }

  async function handleMessage(event: MessageEvent) {
    if (!event.data || !event.data.provider) {
      return;
    }

    const { type, provider, status, data } = event.data;
    if (popup) {
      popup.close();
    }

    // 🎉  We got in!!!
    if (status === 'success') {
      // Refresh the session for latest data on `create` and `login`
      if (type !== 'connect') {
        await refreshSession(data.accessToken, data.refreshToken);
      }

      // Analytics
      const eventType = type === 'connect' ? 'Connected Account' : type === 'login' ? 'Logged In' : 'Created Account';
      analytics.track({ name: eventType, props: { method: provider } });

      dispatch('success');
      return;
    } else if (data.code === 'otp_required') {
      dispatch('needsOtp', { provider, recordId: data.metadata?.['otp_record_id'] });
      return;
    }

    // Some kind of failure.. ❌
    Toasts.push({
      kind: data.kind || 'error',
      duration: 0,
      content: data.content ?? 'There was an unknown issue',
      actions: data.actions || undefined
    });
    dispatch('error');
  }

  export async function onClick() {
    dispatch('click');
    // Disconnection doesn't require going off-platform
    if (type === 'disconnect') {
      try {
        const variables: Record<string, unknown> = {
          provider: provider.toUpperCase()
        };
        if (provider === 'google' || provider === 'discord_guild') {
          variables.id = accountId;
        }
        const { data } = await getApolloClient().mutate({
          mutation: gql`
            mutation DisconnectOAuth($input: AccountOAuthDisconnectInput!) {
              accountOAuthDisconnect(input: $input) {
                result {
                  id
                }
                errors {
                  ... on Error {
                    message
                  }
                }
              }
            }
          `,
          variables: { input: variables }
        });
        if (!data?.accountOAuthDisconnect?.result) {
          Toasts.push({
            kind: 'error',
            content:
              data?.accountOAuthDisconnect?.errors[0]?.message ??
              'There was an unknown error disconnecting your account'
          });
        }
        return;
      } catch (error) {
        captureException(error);
        return;
      }
    }

    // Store a state variable in the LS
    const state = `${uuidv4()}.${PUBLIC_STAGING_ID ?? 'production'}`;
    try {
      window.sessionStorage.setItem('mfy-oauth-state', state);
      window.sessionStorage.setItem('mfy-oauth-path', window.location.href);
    } catch (error) {
      return Toasts.push({
        kind: 'error',
        content: "We weren't able to secure your third-party login. Please try again or use email & password.",
        placement: 'center'
      });
    }

    // Off to {provider} we go! 🚀
    let endpoint: string | null = null;
    switch (provider) {
      case 'discord':
        endpoint = DISCORD_AUTH_URL(type, state);
        break;
      case 'discord_guild':
        endpoint = DISCORD_GUILD_AUTH_URL(type, state);
        break;
      case 'twitch':
        endpoint = TWITCH_AUTH_URL(type, state);
        break;
      case 'google':
        endpoint = GOOGLE_AUTH_URL(type, state, ...providerParams);
        break;
      case 'patreon':
        endpoint = PATREON_AUTH_URL(type, state);
        break;
      case 'apple':
        // Apple is unique! 🤪
        await includeAppleJS();
        if (window.AppleID) {
          window.AppleID.auth.init({
            clientId: APPLE_CLIENT_ID,
            scope: APPLE_SCOPES,
            redirectURI: APPLE_REDIRECT(type),
            state,
            usePopup: true
          });
          try {
            const data = await window.AppleID.auth.signIn();
            const response = await doWork({
              params: { provider: `apple/${type}` },
              url: { searchParams: new URLSearchParams({ code: data['authorization']['id_token'], state }) }
            });
            await handleMessage({ data: response });
          } catch (error) {
            Toasts.push({
              kind: 'error',
              content: 'There was an issue with Sign in with Apple'
            });
          }
        } else {
          Toasts.push({
            kind: 'error',
            content: 'There was an issue with Sign in with Apple'
          });
        }
        break;
      case 'facebook':
        endpoint = FACEBOOK_AUTH_URL(type, state);
        break;
      default:
        break;
    }
    if (endpoint) {
      popup = window.open(endpoint, `${provider}_metafy_oauth`, 'resizable,scrollbars,status,width=400,height=600');
      if (!popup) {
        Toasts.push({
          kind: 'error',
          content: "We couldn't open a popup window for your login. Please allow popups for Metafy."
        });
      }
      window.addEventListener('message', handleMessage, false);
    }
  }

  onDestroy(() => {
    if (popup) {
      window.removeEventListener('message', handleMessage);
      popup.close();
    }
  });

  $: providerComponent =
    provider === 'discord' || provider === 'discord_guild'
      ? faDiscord
      : provider === 'twitch'
        ? faTwitch
        : provider === 'google'
          ? Google
          : provider === 'apple'
            ? faApple
            : provider === 'facebook'
              ? faFacebook
              : provider === 'patreon'
                ? faPatreon
                : faPaypal;
  // We have to explicitly reference the class names so they don't get purged.
  $: providerKlass = transparent
    ? provider === 'discord' || provider === 'discord_guild'
      ? '!bg-brands-discord'
      : provider === 'twitch'
        ? '!bg-brands-twitch'
        : provider === 'google'
          ? '!bg-white shadow-[inset_0px_0px_0px_1px_#747775]'
          : provider === 'apple'
            ? '!bg-brands-apple'
            : provider === 'facebook'
              ? '!bg-brands-facebook'
              : provider === 'patreon'
                ? '!bg-brands-patreon'
                : 'bg-ondark-medium hover:!bg-brands-paypal'
    : provider === 'discord' || provider === 'discord_guild'
      ? 'bg-brands-discord hover:bg-brands-discord'
      : provider === 'twitch'
        ? 'bg-brands-twitch hover:bg-brands-twitch'
        : provider === 'google'
          ? '!bg-white shadow-[inset_0px_0px_0px_1px_#747775]'
          : provider === 'apple'
            ? 'bg-brands-apple hover:bg-brands-apple'
            : provider === 'facebook'
              ? 'bg-brands-facebook hover:bg-brands-facebook'
              : provider === 'patreon'
                ? 'bg-brands-patreon hover:bg-brands-patreon'
                : 'bg-brands-paypal hover:bg-brands-paypal';
</script>

<IconButton
  class="{provider} {providerKlass} {className} oauth-button text-ondark-primary group flex h-12 w-12 items-center justify-center"
  variant="ghost"
  size="lg"
  {disabled}
  {loading}
  icon={providerComponent}
  iconSize="lg"
  on:click={onClick}
/>
